<template>
  <collapse :selected="!!openedFields[item.name]" @toggle="toggle">
    <div class="collapsible-header" slot="collapsible-header">
      <span v-if="item.type === 'destination-account'">* </span><i :class="icon"></i>
      <span class="header-title text-ellipsis" v-if="descriptionRichEdit">{{ item.label }}</span>
      <span class="header-title text-ellipsis" v-else>{{ item.description }}</span>
      <div class="header-buttons">
        <i class="icon-settings" v-if="!isFormLocked"></i>
        <i class="fa fa-files-o" aria-hidden="true"
           @click.stop="duplicate" v-if="!isFormLocked"></i>
        <i class="fa fa-trash" v-if="!isFormLocked" @click.stop="remove"
           aria-hidden="true"></i>
      </div>
    </div>
    <div class="collapsible-body" slot="collapsible-body" v-if="!isFormLocked">
      <slot name="header"></slot>
      <editor-checkbox v-if="showRequired"
                       v-model="item.required"
                       label="Required"
                       :id="item.name + '_required'"/>
      <editor-checkbox v-if="showHideInForm"
                       v-model="item.hide"
                       label="Hide in form"
                       :id="item.name + '_hide'"/>
      <editor-checkbox v-if="showHideInReports"
                       v-model="item.hideInReports"
                       label="Hide in reports"
                       :id="item.name + '_hide_in_reports'"/>
      <editor-checkbox v-if="showHideInPreview"
                       v-model="item.hideInPreview"
                       label="Hide in preview"
                       :disabled="lockHideInPreview"
                       :id="item.name + '_hide_in_preview'"/>
      <editor-checkbox v-if="shareWithParticipant && showShareWithParticipant"
                       v-model="item.by_participant"
                       :label="`To be completed by ${participantSingularName}`"
                       :id="item.name + '_tbc_by_participant'"/>
      <editor-checkbox v-if="hasSharedFields && showIsShared"
                       v-model="item.isShared"
                       label="Share field"
                       :id="item.name + '_is_shared'"
                       @input="clearSharedReadonly"/>
      <editor-checkbox v-if="hasSharedFields && showIsShared"
                       v-model="item.sharedReadOnly"
                       label="Share as read-only"
                       :disabled="!item.isShared"
                       :id="item.name + '_share_readonly'"/>

      <template v-if="descriptionRichEdit">
        <div class="form-group">
          <vue-editor :id="item.name" v-model="item.description"></vue-editor>
        </div>

        <div class="form-group">
          <label :for="item.name + '-label'">Description</label>
          <input :id="item.name + '-label'" class="form-control" type="text"
                 v-model="item.label">
        </div>
      </template>


      <div class="form-group" v-if="!descriptionRichEdit">
        <label :for="item.name + '-description'"><b>Description</b></label>
        <input :id="item.name + '-description'"
               ref="descriptionInput"
               class="form-control"
               type="text"
               v-model="item.description"
        >
      </div>

      <div class="form-group">
        <label :for="item.name + '-grid'">Grid size</label>
        <select :id="item.name + '-grid'"
                v-model="item.grid"
                class="form-control">
          <option v-for="index in 12 "
                  :key="index">
            {{ index }}
          </option>
        </select>
      </div>

      <!-- TODO: check if this is required -->
      <div v-if="item.type === 'switch'">
        <div class="form-group">
          <label class="active" :for="item.labelActive + '-labelActive'">Label
            active</label>
          <input :id="item.name + '-labelActive'" class="form-control"
                 type="text"
                 v-model="item.labelActive">
        </div>
        <div class="form-group">
          <label class="active" :for="item.name + '-labelInactive'">Label
            inactive</label>
          <input :id="item.name + '-labelInactive'" class="form-control"
                 type="text"
                 v-model="item.labelInactive">
        </div>
      </div>

      <div v-if="item.type === 'destination-account'">
        <p>This field can't be optional. It's always required.</p>
      </div>

      <!-- TODO: check if this is required -->
      <div v-if="['select_case_manager_secondary', 'select2'].includes(item.name)" class="form-group">
        <label>Select2</label>
      </div>

      <slot></slot>
    </div>

  </collapse>
</template>

<script>
import { computed, set, inject, ref, nextTick } from 'vue'

import formBuilderIcons from '@/utils/formBuilder/icons';
import Collapse from '@/components/Collapse';
import EditorCheckbox from '@/components/formbuilder/editors/EditorCheckbox';
import participantDisplayName from '@/mixins/participantDisplayName';

export default {
  props: {
    item: {
      type: Object,
      required: true
    },
    descriptionRichEdit: {
      type: Boolean,
      default: false,
    },
    showRequired: {
      type: Boolean,
      default: false,
    },
    showHideInForm: {
      type: Boolean,
      default: false,
    },
    showHideInReports: {
      type: Boolean,
      default: false,
    },
    showHideInPreview: {
      type: Boolean,
      default: false,
    },
    lockHideInPreview: {
      type: Boolean,
      default: false,
    },
    // TODO: move this and showIsShared to vuex
    showShareWithParticipant: {
      type: Boolean,
      default: false,
    },
    showIsShared: {
      type: Boolean,
      default: false,
    },
  },
  components: {Collapse, EditorCheckbox},
  emits: ['duplicate', 'remove'],
  mixins: [
    participantDisplayName
  ],

  setup(props, { emit }) {
    const icon = computed(() => formBuilderIcons[props.item.type])
    const opened = ref(false)
    const descriptionInput = ref()

    // TODO: move from events to vuex
    const duplicate = () => {
      emit('duplicate', props.item)
    }

    const remove = () => {
      emit('remove', props.item)
    }

    const clearSharedReadonly = () => {
      set(props.item, 'sharedReadOnly', false)
    }

    const toggle = (e) => {
      emit('toggle', e);

      if (descriptionInput.value && descriptionInput.value.focus) {
        nextTick(() => descriptionInput.value.focus())
      }
    }

    // TODO: move this to vuex
    const isFormLocked = inject('isFormLocked');
    const hasSharedFields = inject('hasSharedFields');
    const shareWithParticipant = inject('shareWithParticipant');
    const openedFields = inject('openedFields');

    return {
      icon,
      duplicate,
      remove,
      toggle,
      opened,
      isFormLocked,
      shareWithParticipant,
      hasSharedFields,
      openedFields,
      clearSharedReadonly,
      descriptionInput,
    }
  }
}
</script>


<style scoped lang="scss">

i {
  cursor: pointer;

  & :hover {
    color: #d09f23;
  }
}

.collapsible-header {
  display: flex;
  align-items: center
}

.header-title {
  margin-left: 4px;
  flex-shrink: 2;
}

.header-buttons {
  margin-left: auto;
  white-space: nowrap;
}

</style>



// WEBPACK FOOTER //
// FormBuilderEditorField.vue?7936c88a